import {
  EMAIL_MARKETING_CHECKLIST_URL,
  KNOWLEDGE_CENTER_URL,
} from "@/utils/const";
import { doesUserHaveScope } from "@/utils/doesUserHaveScope";
import { IFeatureFlags, isFeatureFlagEnabled } from "@/utils/featureFlags";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faAddressCard,
  faBarsProgress,
  faBoxesStacked,
  faBuildingUser,
  faChartLine,
  faCheckSquare,
  faClipboardListCheck,
  faCreditCard,
  faEnvelopeCircleCheck,
  faFileLines,
  faGrid,
  faHourglassClock,
  faMessageSms,
  faMonitorWaveform,
  faNfcMagnifyingGlass,
  faShop,
  faToolbox,
  faWindow,
} from "@fortawesome/pro-duotone-svg-icons";
import { t } from "i18next";

type ISidebarItem = Omit<INavItem, "sidebar"> & {
  icon: IconProp;
};

export interface INavItem {
  title: string;
  url: string;
  external?: boolean;
  newTab?: boolean;
  locked?: () => boolean;
  hidden?: () => boolean;
  isMobileOnly?: boolean;
  sidebar: ISidebarItem[];
}

interface IProps {
  scopes: IUserScope[];
  flags: IFeatureFlags;
}

const getNavigationItems = ({ scopes, flags }: IProps): INavItem[] => [
  {
    title: t("layouts.navigation.definition.email.title"),
    url: "/marketing",
    locked: () => !doesUserHaveScope("LIST_CONTACTS", scopes),
    sidebar: [
      {
        icon: faChartLine,
        title: t("layouts.navigation.definition.email.sidebar.dashboard"),
        url: "/dashboard",
      },
      {
        icon: faGrid,
        title: t("layouts.navigation.definition.email.sidebar.usage"),
        url: "/usage",
      },
      {
        icon: faAddressCard,
        title: t(
          "layouts.navigation.definition.email.sidebar.marketableAudiences",
        ),
        url: "/marketable-audiences",
      },
      {
        icon: faNfcMagnifyingGlass,
        title: t("layouts.navigation.definition.email.sidebar.auditLogs"),
        url: "/audit-logs",
      },
      {
        icon: faFileLines,
        title: t(
          "layouts.navigation.definition.email.sidebar.complianceReports",
        ),
        url: "/compliance-reports",
      },
      {
        icon: faEnvelopeCircleCheck,
        title: t(
          "layouts.navigation.definition.email.sidebar.emailMarketingChecklist",
        ),
        url: EMAIL_MARKETING_CHECKLIST_URL,
        external: true,
        newTab: true,
      },
    ],
  },
  {
    title: t("layouts.navigation.definition.sms.title"),
    url: "/sms",
    locked: () => !doesUserHaveScope("LIST_SMS_CONTACTS", scopes),
    sidebar: [
      {
        icon: faMonitorWaveform,
        title: t("layouts.navigation.definition.sms.sidebar.healthCheck"),
        url: "/health-check",
        hidden: () =>
          !isFeatureFlagEnabled(flags["collect-sms-health-enabled"]),
      },
      {
        icon: faHourglassClock,
        title: t(
          "layouts.navigation.definition.sms.sidebar.healthCheckHistory",
        ),
        url: "/health-check-history/shopify",
        hidden: () =>
          !isFeatureFlagEnabled(flags["collect-sms-health-history-enabled"]),
      },
      {
        icon: faAddressCard,
        title: t(
          "layouts.navigation.definition.sms.sidebar.marketableAudiences",
        ),
        url: "/marketable-audiences",
      },
      {
        icon: faNfcMagnifyingGlass,
        title: t("layouts.navigation.definition.sms.sidebar.auditLogs"),
        url: "/audit-logs",
      },
      {
        icon: faFileLines,
        title: t("layouts.navigation.definition.sms.sidebar.complianceReports"),
        url: "/compliance-reports",
      },
      {
        icon: faMessageSms,
        title: t("layouts.navigation.definition.sms.sidebar.feature"),
        url: "/request",
        hidden: () => !!doesUserHaveScope("LIST_SMS_CONTACTS", scopes),
      },
    ],
  },
  {
    title: t("layouts.navigation.definition.comply.title"),
    url: "/comply",
    locked: () =>
      !doesUserHaveScope(["EDIT_POLICIES", "GENERATE_POLICIES"], scopes, false),
    hidden: () =>
      !doesUserHaveScope(["EDIT_POLICIES", "GENERATE_POLICIES"], scopes, false),
    sidebar: [
      {
        url: "/privacy-centre",
        title: t("layouts.navigation.definition.comply.privacyCenter"),
        icon: faWindow,
      },
      {
        url: "/policies",
        title: t("layouts.navigation.definition.comply.policies"),
        icon: faClipboardListCheck,
      },
    ],
  },
  {
    title: t("layouts.navigation.definition.settings.title"),
    url: "/settings",
    hidden: () => isFeatureFlagEnabled(flags["web-app-mat-onboarding-enabled"]),
    sidebar: [
      {
        icon: faCheckSquare,
        title: t(
          "layouts.navigation.definition.settings.sidebar.widgetSettings",
        ),
        url: "/widget",
        hidden: () => !doesUserHaveScope("VIEW_WIDGET_SETTINGS", scopes),
      },
      {
        icon: faBoxesStacked,
        title: t(
          "layouts.navigation.definition.settings.sidebar.integrationSettings",
        ),
        url: "/integration",
        hidden: () => !doesUserHaveScope("VIEW_INTEGRATION_SETTINGS", scopes),
      },
      {
        icon: faBuildingUser,
        title: t(
          "layouts.navigation.definition.settings.sidebar.companyProfile",
        ),
        url: "/company-profile",
      },
      {
        icon: faCreditCard,
        title: t("layouts.navigation.definition.settings.sidebar.billing"),
        url: "/billing",
      },
      {
        icon: faBarsProgress,
        title: t(
          "layouts.navigation.definition.settings.sidebar.userManagement",
        ),
        url: "/user-management",
      },
    ],
  },
  {
    title: t("layouts.navigation.definition.integrations.title"),
    url: "/integrations",
    hidden: () =>
      !isFeatureFlagEnabled(flags["web-app-mat-onboarding-enabled"]),
    sidebar: [
      {
        icon: faToolbox,
        title: t(
          "layouts.navigation.definition.integrations.sidebar.matSettings",
        ),
        url: "/mat-settings",
      },
      {
        icon: faShop,
        title: t(
          "layouts.navigation.definition.integrations.sidebar.shopSettings",
        ),
        url: "/shop-settings",
      },
    ],
  },
  {
    title: t("layouts.navigation.definition.profile.title"),
    url: "/profile",
    isMobileOnly: true,
    sidebar: [
      {
        icon: faBuildingUser,
        title: t("layouts.navigation.definition.profile.sidebar.company"),
        url: "/company",
      },
      {
        icon: faCreditCard,
        title: t("layouts.navigation.definition.profile.sidebar.billing"),
        url: "/billing",
      },
      {
        icon: faBarsProgress,
        title: t(
          "layouts.navigation.definition.profile.sidebar.userManagement",
        ),
        url: "/user-management",
      },
    ],
  },
  {
    title: t("layouts.navigation.definition.help.title"),
    url: "/help",
    isMobileOnly: true,
    sidebar: [
      {
        icon: faCheckSquare,
        title: t("layouts.navigation.definition.help.sidebar.knowledgeBase"),
        external: true,
        newTab: true,
        url: KNOWLEDGE_CENTER_URL,
      },
      {
        icon: faCheckSquare,
        title: t("layouts.navigation.definition.help.sidebar.contactSupport"),
        url: "/support",
      },
    ],
  },
];

export default getNavigationItems;
